// ClientAutocomplete — sdílený fuzzy autocomplete komponent.
// Pouziti: LogStBonus, LogCash, LogExpired (modaly Pridat zaznam).
//
// Match logika:
//   - Diakritika ignorovana (NFD normalize + odstranění combining marks)
//   - Vsechny tokeny (mezery delene) musi byt v option - libovolne poradi
//   - Tedy "bartkov" -> Lucia Bartkova, "bartk lucia" -> taky najde, "lucia" -> taky
//
// Props:
//   value: string             - aktualni hodnota inputu
//   onChange: (val) => void   - callback pri zmene
//   options: string[]         - seznam jmen klientu (z /api/clients)
//   accentColor?: string      - "indigo" | "emerald" | "violet" (default indigo)
//   placeholder?: string      - default "Začni psát jméno nebo příjmení…"

import React, { useMemo, useState, useRef } from "react";
import { PlovouciNabidka } from "./ui.jsx";

export function normalizeLoose(s) {
  return String(s || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")  // odstrani diacritic combining marks
    .toLowerCase()
    .replace(/[^a-z0-9 ]+/g, " ")
    .replace(/\s+/g, " ").trim();
}

const ACCENT_CLASSES = {
  indigo:  { focus: "focus:border-plum-500 focus:ring-plum-100", hi: "bg-plum-50 text-plum-900" },
  emerald: { focus: "focus:border-emerald-500 focus:ring-emerald-100", hi: "bg-emerald-50 text-emerald-900" },
  violet:  { focus: "focus:border-brand-500 focus:ring-brand-100", hi: "bg-brand-50 text-brand-900" },
  amber:   { focus: "focus:border-amber-500 focus:ring-amber-100", hi: "bg-amber-50 text-amber-900" },
};

export default function ClientAutocomplete({ value, onChange, options, accentColor = "indigo", placeholder, autoFocus, inputClassName = "" }) {
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(0);
  const inputRef = useRef(null);
  const obalRef = useRef(null);
  const accent = ACCENT_CLASSES[accentColor] || ACCENT_CLASSES.indigo;

  const matches = useMemo(() => {
    const q = normalizeLoose(value);
    if (!q) return options.slice(0, 8);
    const tokens = q.split(" ").filter(Boolean);
    return options.filter((opt) => {
      const optNorm = normalizeLoose(opt);
      // Vsechny tokeny musi byt v option (libovolne poradi - najde i jen prijmeni)
      return tokens.every((t) => optNorm.includes(t));
    }).slice(0, 8);
  }, [value, options]);

  // FW-152 (14. 10. 2026): na mobilu se při psaní posouvá stránka (klávesnice)
  // a nabídka se zavírala už při ztrátě fokusu nebo při prvním doteku prstem
  // (výběr byl na touchstart, takže posun prstem vybral nebo zavřel). Teď:
  // nabídka je plovoucí (portál, drží se pole i při posunu), výběr až klepnutím,
  // zavírá se jen po výběru, klepnutím mimo, Esc nebo přechodem tabulátorem jinam.
  const vyber = (opt) => { onChange(opt); setOpen(false); };
  const doZorne = () => {
    const el = inputRef.current;
    if (!el || typeof window === "undefined" || window.innerWidth >= 768) return;
    // počkat, až se otevře klávesnice, pak pole posunout do viditelné části
    setTimeout(() => { try { el.scrollIntoView({ block: "center", behavior: "smooth" }); } catch (_) {} }, 300);
  };

  return (
    <div ref={obalRef} className="relative">
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => { onChange(e.target.value); setOpen(true); setHighlighted(0); }}
        onFocus={() => { setOpen(true); doZorne(); }}
        onBlur={(e) => {
          // Zavřít jen při přechodu na jiné pole (tabulátor). Klepnutí mimo řeší PlovouciNabidka.
          const kam = e.relatedTarget;
          if (kam && !(kam instanceof Element && kam.closest("[data-plovouci-nabidka]"))) setOpen(false);
        }}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown") { e.preventDefault(); setOpen(true); setHighlighted((h) => Math.min(h + 1, matches.length - 1)); }
          if (e.key === "ArrowUp") { e.preventDefault(); setHighlighted((h) => Math.max(h - 1, 0)); }
          if (e.key === "Enter" && open && matches[highlighted]) { e.preventDefault(); vyber(matches[highlighted]); }
          if (e.key === "Escape") { setOpen(false); }
        }}
        placeholder={placeholder || "Začni psát jméno nebo příjmení…"}
        autoComplete="off"
        autoFocus={autoFocus}
        className={`w-full px-3 py-2.5 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 ${accent.focus} ${inputClassName}`}
      />
      <PlovouciNabidka kotva={obalRef} otevreno={open && matches.length > 0} onZavrit={() => setOpen(false)} minSirka={200} maxVyska={240} className="shadow-lg">
        {matches.map((opt, idx) => (
          <button
            key={opt}
            type="button"
            tabIndex={-1}
            // mousedown nesmí vzít fokus z pole (jinak se na mobilu zavře klávesnice a nabídka uskočí)
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => vyber(opt)}
            onMouseEnter={() => setHighlighted(idx)}
            className={`w-full text-left px-3 py-2 min-h-[44px] md:min-h-0 text-base md:text-sm transition-colors ${
              idx === highlighted ? accent.hi : "text-slate-700 hover:bg-slate-50"
            }`}
          >
            {opt}
          </button>
        ))}
      </PlovouciNabidka>
    </div>
  );
}

// Cesky tooltip pro pouziti vedle labelu "Klient" v modalech.
// Pouziti:  <InfoTip text={CLIENT_AUTOCOMPLETE_TOOLTIP} />
export const CLIENT_AUTOCOMPLETE_TOOLTIP = "Začni psát jméno nebo příjmení. Najde i podle příjmení (např. \"bartkov\" -> Lucia Bartková). Diakritika se ignoruje.";
